import { useState, useEffect, useCallback, useMemo } from "react";
import { isSystemPromptFeatureEnabled } from "../services/config";
import { promptService } from "../services/prompt";
import { getUserEmail } from "../../../auth/okta";

export const useSystemPrompt = () => {
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [savedPrompt, setSavedPrompt] = useState<string | null>(null);
  const [freshPrompt, setFreshPrompt] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);

  const hasCustom = useMemo(() => {
    return savedPrompt !== null && savedPrompt !== freshPrompt;
  }, [savedPrompt, freshPrompt]);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        const email = await getUserEmail();
        setUserEmail(email);
        setIsEnabled(isSystemPromptFeatureEnabled(email));
      } catch (error) {
        console.error("Failed to check system prompt feature:", error);
        setIsEnabled(false);
      }
    };

    checkFeature();
  }, []);

  useEffect(() => {
    if (!userEmail || !isEnabled) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const [saved, fresh] = await Promise.all([promptService.getSavedPrompt(userEmail), promptService.getFreshPrompt()]);

        setSavedPrompt(saved);
        setFreshPrompt(fresh);
      } catch (error) {
        console.error("Failed to load system prompt data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [userEmail, isEnabled]);

  const handleSave = useCallback(
    async (prompt: string): Promise<void> => {
      if (!userEmail) {
        console.error("Cannot save prompt: user email not available");
        return;
      }

      await promptService.savePrompt(prompt, userEmail);
      setSavedPrompt(prompt.trim() || null);
    },
    [userEmail]
  );

  const handleReset = useCallback(async (): Promise<void> => {
    if (!userEmail) {
      console.error("Cannot reset prompt: user email not available");
      return;
    }

    setIsLoading(true);
    try {
      const fresh = await promptService.resetSystemPrompt(userEmail);
      setSavedPrompt(null);
      setFreshPrompt(fresh);
    } finally {
      setIsLoading(false);
    }
  }, [userEmail]);

  const openPopup = useCallback(() => setIsPopupOpen(true), []);
  const closePopup = useCallback(() => setIsPopupOpen(false), []);

  return {
    isEnabled,
    isPopupOpen,
    savedPrompt,
    freshPrompt,
    isLoading,
    hasCustom,
    userEmail,
    openPopup,
    closePopup,
    handleSave,
    handleReset,

    getSystemPrompt: () => savedPrompt
  };
};
